/**
 * useBreadcrumb composable
 *
 * Manages the breadcrumb navigation trail so drill-down pages
 * (Activity → Workgroup → Instance Workgroup) can show users
 * where they are and let them navigate back to any parent level.
 *
 * Usage:
 *   Source page (before router.push):
 *     pushCrumb({ label: 'Workgroup X', icon: 'group_work', route: { name: '...' } })
 *
 *   Destination page (on mount, for direct-URL access):
 *     if (!trail.value.length) setTrail([...])
 */
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

export function useBreadcrumb() {
    const store = useStore();
    const router = useRouter();

    /** Reactive breadcrumb trail */
    const trail = computed(() => store.getters.breadcrumbTrail || []);

    /** Replace the full trail */
    function setTrail(crumbs) {
        store.commit("SET_BREADCRUMB_TRAIL", crumbs);
    }

    /** Append a single crumb to the end of the trail */
    function pushCrumb(crumb) {
        store.commit("PUSH_BREADCRUMB", crumb);
    }

    /** Empty the trail */
    function clearTrail() {
        store.commit("CLEAR_BREADCRUMBS");
    }

    /**
     * Navigate to an ancestor crumb.
     * Truncates the trail to that index and routes to the crumb's route.
     */
    function navigateTo(index) {
        const crumb = trail.value[index];
        if (!crumb?.route) return;

        // Keep crumbs up to and including the clicked one
        store.commit("SET_BREADCRUMB_TRAIL", trail.value.slice(0, index + 1));
        router.push(crumb.route);
    }

    return { trail, setTrail, pushCrumb, clearTrail, navigateTo };
}
